import { useSnackbar } from 'notistack';
import { alpha } from '@mui/material/styles';
import { useContext, useEffect, useMemo, useState } from 'react';
import { rolePermission } from '../interfaces/ApplicationInterfaces';
import { AdminContext } from '../contexts/AdminContext';
import { COSMOS_API, GET_ENDPOINT, POST_ENDPOINT } from '../api/API';
import { Organization } from '../interfaces/APIInterface';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Stack from '@mui/material/Stack';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import SendIcon from '@mui/icons-material/Send';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import { UserSearchResult } from '../interfaces/UserInterfaces';
import { isNullUndefEmpty } from '../ts/common';

interface EnhancedTableProps {
  numSelected: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { onSelectAllClick, numSelected, rowCount } = props;

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              'aria-label': 'select all users',
            }}
          />
        </TableCell>
        {['Email', 'First Name', 'Last Name', 'Country'].map(
          (colName, index) => (
            <TableCell
              key={colName}
              align={'left'}
              padding={index === 0 ? 'none' : 'normal'}
            >
              {colName}
            </TableCell>
          )
        )}
      </TableRow>
    </TableHead>
  );
}

interface EnhancedTableToolbarProps {
  numSelected: number;
  handleAction: () => void;
}

function EnhancedTableToolbar(props: EnhancedTableToolbarProps) {
  const { numSelected, handleAction } = props;

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: '1 1 100%' }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          sx={{ flex: '1 1 100%' }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          Users
        </Typography>
      )}
      {numSelected > 0 ? (
        <Tooltip title="Send Invite">
          <IconButton onClick={handleAction}>
            <SendIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <></>
      )}
    </Toolbar>
  );
}

const ResendInvite = () => {
  const { adminCredentials, getToken } = useContext(AdminContext);
  const [organizations, setOrganizations] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [applications, setApplications] = useState<
    Array<{ id: string; name: string, isGlobal: boolean }>
  >([]);
  const [appOptionsForSelectedOrg, setAppOptionsForSelectedOrg] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [selectedOrg, setSelectedOrg] = useState<string>(
    adminCredentials.organizationList?.length > 1 ? "" : adminCredentials.organizationId
  );
  const [selectedApp, setSelectedApp] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [users, setUsers] = useState<Array<UserSearchResult>>([]);
  const [usersToSend, setUsersToSend] = useState<Set<string>>(
    new Set<string>()
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchValidation, setSearchValidation] = useState({
    organizationId: '',
    applicationId: '',
    email: '',
  });
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [noUsersMessage, setNoUsersMessage] = useState("Please submit a search query");

  useEffect(() => {
    /* https://stackoverflow.com/a/60907638 */
    let isMounted = true;

    // Populate Org List
    if (organizations === undefined || organizations.length === 0) {
      if (adminCredentials.organizationList && (adminCredentials.organizationList.length > 0)) {
        setOrganizations([...adminCredentials.organizationList.map(adminOrg => { return { id: adminOrg.id, name: adminOrg.name } })]);
        setSelectedOrg(adminCredentials.organizationList[0].id);
      } else {
        setOrganizations([]);
        setSelectedOrg("");
      }
    }

    // Populate App List
    if (
      applications === undefined ||
      applications === null ||
      applications.length === 0
    ) {
      if (
        adminCredentials.isSuperAdmin ||
        adminCredentials.isOrganizationAdmin
      ) {
        let apps: Array<{ id: string; name: string, isGlobal: boolean }> = [];
        apps = Object.keys(adminCredentials.allApplications).reduce(function (
          result,
          appId
        ) {
          result.push({
            id: appId,
            name: adminCredentials.allApplications[appId]?.name,
            isGlobal: adminCredentials.allApplications[appId]?.isGlobal
          });
          return result;
        },
        apps);
        setApplications([...apps]);
      } else {
        let apps: Array<{ id: string; name: string, isGlobal: boolean }> =
          adminCredentials.adminApplications
            .filter((value) =>
              rolePermission(
                'createNewUsers',
                value.role,
                adminCredentials.roleSpecificPermissions,
                adminCredentials.isSuperAdmin
              )
            )
            .map((key: any) => {
              return {
                name: adminCredentials.allApplications[key.id]?.name,
                id: key.id,
                isGlobal: adminCredentials.allApplications[key.id]?.isGlobal
              };
            });
        setApplications([...apps]);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [adminCredentials]);

  useEffect(() => {
    if (!isNullUndefEmpty(selectedOrg)) {
      updateOrgData();
    }
  }, [selectedOrg, applications]);

  async function updateOrgData() {
    let orgDataResp = await COSMOS_API.POST(
      { id: selectedOrg },
      POST_ENDPOINT.GetOrganizationData,
      getToken()
    );

    if (
      orgDataResp.ok &&
      orgDataResp.respBody !== undefined &&
      orgDataResp.respBody !== null
    ) {
      let appsForOrg = applications
        .filter((app) => (orgDataResp.respBody.appList.includes(app.id) || (app.isGlobal)))
        .sort(
          (a: { id: string; name: string }, b: { id: string; name: string }) =>
            a.name <= b.name ? -1 : 1
        );
      setAppOptionsForSelectedOrg(appsForOrg);

      if (
        appsForOrg.length === 1 ||
        (appsForOrg.length >= 1 &&
          !appsForOrg.some((appOption) => appOption.id === selectedApp))
      ) {
        setSelectedApp(appsForOrg[0].id);
      } else if (appsForOrg.length === 0) {
        setSelectedApp('');
      }
    }
  }

  async function findUsers() {
    enqueueSnackbar('Fetching users please wait.', { variant: 'info' });
    setUsers([]);

    let body = {
      organizationId: selectedOrg,
      applicationId: selectedApp,
      email: email,
    };
    let resp = await COSMOS_API.POST(
      body,
      POST_ENDPOINT.GetUsersWithNoLogins,
      getToken()
    );

    if (resp.ok) {
      enqueueSnackbar('Successfully fetched users.', { variant: 'success' });
      setUsers(resp.respBody.users);
      setNoUsersMessage('No users found matching search query');
      setSearchValidation(body);
    } else {
      setNoUsersMessage('There was an error, please try again');
      enqueueSnackbar('Error fetching users.', { variant: 'error' });
    }
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - users.length) : 0;

  const visibleRows = useMemo(
    () =>
      users
        .slice()
        .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, users]
  );

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = users.map((n) => n.id);
      setUsersToSend(new Set<string>(newSelected));
      return;
    }
    setUsersToSend(new Set<string>());
  };

  const selectUser = (event: React.MouseEvent<unknown>, targetId: string) => {
    const removeId = usersToSend.has(targetId);
    let newSelected = structuredClone(usersToSend);

    if (removeId) {
      newSelected.delete(targetId);
    } else {
      newSelected.add(targetId);
    }

    setUsersToSend(newSelected);
  };

  async function sendInviteToSelected() {
    const body = {
      searchValidation: searchValidation,
      users: Array.from(usersToSend),
    };

    enqueueSnackbar('Sending invite emails to users please wait.', {
      variant: 'info',
    });

    let resp = await COSMOS_API.POST(
      body,
      POST_ENDPOINT.ResendInvite,
      getToken()
    );

    if (resp.ok) {
      enqueueSnackbar('Successfully sent invites to users.', {
        variant: 'success',
      });
    } else {
      enqueueSnackbar('Error sending invites users.', { variant: 'error' });
    }
  }

  const autoCompleteSingleSelect = (
    id: string,
    label: string,
    selectedVal: string,
    setSelectedVal:
      | React.Dispatch<React.SetStateAction<string>>
      | ((arg0: string) => void),
    possibleVals: Array<{ id: string; name: string }>
  ) => {
    return (
      <Autocomplete
        id={id}
        fullWidth
        disableClearable
        value={possibleVals.find((val) => val.id === selectedVal)}
        isOptionEqualToValue={(
          option: { id: string; name: string },
          value: { id: string; name: string }
        ) => option.id === value.id}
        options={possibleVals}
        getOptionLabel={(option: { id: string; name: string }) => option.name}
        renderInput={(params) => <TextField {...params} label={label} />}
        onChange={(_: any, value: { id: string; name: string } | null) => {
          if (value === null) return;
          setSelectedVal(value.id);
        }}
      />
    );
  };

  return (
    <>
      <div className="full-section-container">
        <Container>
          <Stack spacing={4}>
            <Stack spacing={2}>
              {adminCredentials.multiOrg &&
                organizations.length > 1 &&
                autoCompleteSingleSelect(
                  'org',
                  'Organization',
                  selectedOrg,
                  setSelectedOrg,
                  organizations
                )}
              {appOptionsForSelectedOrg.length > 1 ? (
                autoCompleteSingleSelect(
                  'app',
                  'Application',
                  selectedApp,
                  setSelectedApp,
                  appOptionsForSelectedOrg
                )
              ) : (
                <Typography>
                  Application:{' '}
                  {appOptionsForSelectedOrg.find(
                    (app) => app.id === selectedApp
                  )?.name ?? 'No Active Applications'}
                </Typography>
              )}
              <TextField
                label="Email Address"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
              />
              <Button
                disabled={(!selectedOrg) || selectedOrg === '' || selectedApp === ''}
                onClick={findUsers}
              >
                Find Users
              </Button>
            </Stack>
            <Box sx={{ width: '100%' }}>
              <Paper sx={{ width: '100%', mb: 2 }}>
                <EnhancedTableToolbar
                  numSelected={usersToSend.size}
                  handleAction={sendInviteToSelected}
                />
                <TableContainer>
                  <Table
                    sx={{ minWidth: 750 }}
                    aria-labelledby="tableTitle"
                    size={'small'}
                  >
                    <EnhancedTableHead
                      numSelected={usersToSend.size}
                      onSelectAllClick={handleSelectAllClick}
                      rowCount={users.length}
                    />
                    <TableBody>
                      {users.length > 0 ? visibleRows.map((user, index) => {
                        const isItemSelected = usersToSend.has(user.id);
                        const labelId = `enhanced-table-checkbox-${index}`;

                        return (
                          <TableRow
                            hover
                            onClick={(event) => selectUser(event, user.id)}
                            role="checkbox"
                            aria-checked={isItemSelected}
                            tabIndex={-1}
                            key={user.id}
                            selected={isItemSelected}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                color="primary"
                                checked={isItemSelected}
                                inputProps={{
                                  'aria-labelledby': labelId,
                                }}
                              />
                            </TableCell>
                            <TableCell
                              component="th"
                              id={labelId}
                              scope="row"
                              padding="none"
                            >
                              {user.email}
                            </TableCell>
                            <TableCell align="right">
                              {user.firstName}
                            </TableCell>
                            <TableCell align="right">{user.lastName}</TableCell>
                            <TableCell align="right">{user.country}</TableCell>
                          </TableRow>
                        );
                      }) : <TableRow
                      style={{
                        height: 33,
                      }}
                    >
                      <TableCell colSpan={6}>
                        {noUsersMessage}
                        </TableCell>
                    </TableRow>}
                      {emptyRows > 0 && (
                        <TableRow
                          style={{
                            height: 33 * emptyRows,
                          }}
                        >
                          <TableCell colSpan={6} />
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={users.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Paper>
            </Box>
          </Stack>
        </Container>
      </div>
    </>
  );
};

export default ResendInvite;
